import { useState } from "react";
import { toast } from "sonner";
import { api, fmtErr } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { getTheme, setTheme } from "@/lib/theme";
import { PageHeader } from "@/components/bits";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { Sun, Moon, AlertTriangle, CreditCard } from "lucide-react";

export default function Settings() {
  const { user } = useAuth();
  const [theme, setThemeState] = useState(getTheme());
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [password, setPassword] = useState("");
  const [ack, setAck] = useState(false);
  const [busy, setBusy] = useState(false);

  function choose(t) {
    setTheme(t);
    setThemeState(t);
  }

  async function deleteAccount(e) {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post("/account/delete", { password });
      toast.success(
        data.subscription_cancelled
          ? "Kündigung ausgesprochen (wirksam zum Monatsende) — Konto gelöscht"
          : "Konto gelöscht"
      );
      window.location.href = "/login";
    } catch (e2) {
      toast.error(fmtErr(e2));
    } finally {
      setBusy(false);
    }
  }

  const isTenant = user?.role === "TENANT_ADMIN";

  return (
    <div data-testid="settings-page" className="max-w-2xl">
      <PageHeader title="Einstellungen" sub={user?.email} />

      <div className="panel p-5 mb-6" data-testid="settings-theme-panel">
        <div className="mono-label mb-4">Design</div>
        <div className="grid grid-cols-2 gap-3">
          <button
            onClick={() => choose("dark")}
            className={`flex items-center gap-3 p-4 rounded-sm border transition-colors duration-150 ${
              theme === "dark" ? "border-cyan-500/60 bg-cyan-500/5" : "border-border hover:border-cyan-500/30"
            }`}
            data-testid="settings-theme-dark"
          >
            <span className="w-8 h-8 rounded-sm bg-[#090D16] border border-slate-700 flex items-center justify-center shrink-0">
              <Moon size={14} className="text-cyan-400" />
            </span>
            <span className="text-left">
              <span className="block text-sm font-semibold text-slate-200">Dunkel</span>
              <span className="block text-[11px] text-slate-300">NOC / Command Center</span>
            </span>
          </button>
          <button
            onClick={() => choose("light")}
            className={`flex items-center gap-3 p-4 rounded-sm border transition-colors duration-150 ${
              theme === "light" ? "border-cyan-500/60 bg-cyan-500/5" : "border-border hover:border-cyan-500/30"
            }`}
            data-testid="settings-theme-light"
          >
            <span className="w-8 h-8 rounded-sm bg-white border border-slate-300 flex items-center justify-center shrink-0">
              <Sun size={14} className="text-cyan-600" />
            </span>
            <span className="text-left">
              <span className="block text-sm font-semibold text-slate-200">Hell</span>
              <span className="block text-[11px] text-slate-300">Klassisches Tagesdesign</span>
            </span>
          </button>
        </div>
      </div>

      <div className="panel p-5 mb-6" data-testid="settings-subscription-panel">
        <div className="mono-label mb-4 flex items-center gap-2">
          <CreditCard size={13} /> Abonnement
        </div>
        <div className="flex items-center justify-between gap-4">
          <div>
            <div className="font-display font-semibold text-slate-100" data-testid="settings-plan-name">Professional</div>
            <div className="text-xs text-slate-300 mt-1 leading-relaxed">
              Monatlich kündbar — eine Kündigung wird zum Ende des laufenden Monats wirksam.
            </div>
          </div>
          <span className="font-mono text-[10px] uppercase tracking-widest text-emerald-400 border border-emerald-500/40 bg-emerald-500/10 px-2 py-1 rounded-sm shrink-0" data-testid="settings-plan-status">
            Aktiv
          </span>
        </div>
      </div>

      <div className="panel p-5 !border-red-500/30" data-testid="settings-danger-panel">
        <div className="mono-label mb-3 flex items-center gap-2 !text-red-400">
          <AlertTriangle size={13} /> Gefahrenbereich
        </div>
        <p className="text-xs text-slate-300 leading-relaxed mb-4">
          {isTenant
            ? "Bei der Kontolöschung sprichst du gleichzeitig die Kündigung deines Abonnements aus (monatlich kündbar, wirksam zum Monatsende). Dein Zugang wird sofort gesperrt und die Geräte-Credentials deines Mandanten werden widerrufen. Der Verlauf (Audit-Log) bleibt aus Nachweisgründen erhalten."
            : "Dein Benutzerkonto wird unwiderruflich gelöscht."}
        </p>
        <button
          onClick={() => setConfirmOpen(true)}
          className="btn-ghost !border-red-500/50 !text-red-400 hover:!bg-red-500/10"
          data-testid="settings-delete-account-button"
        >
          {isTenant ? "Kündigen & Konto löschen" : "Konto löschen"}
        </button>
      </div>

      <Dialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <DialogContent className="bg-popover border-border max-w-md" data-testid="delete-account-dialog">
          <DialogTitle className="font-display font-bold text-slate-100 mb-1">Endgültig löschen?</DialogTitle>
          <p className="text-xs text-slate-300 leading-relaxed mb-4">
            {isTenant
              ? "Die Kündigung wird zum Monatsende wirksam. Dein Konto und der Zugang werden sofort deaktiviert, alle Geräte deines Mandanten werden vom Netz genommen (Credentials widerrufen)."
              : "Dein Konto wird sofort gelöscht."}
          </p>
          <form onSubmit={deleteAccount} className="space-y-4">
            <div>
              <label className="mono-label block mb-1.5" htmlFor="delete-password">Passwort bestätigen</label>
              <input
                id="delete-password"
                type="password"
                required
                autoComplete="current-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="input-dark"
                data-testid="delete-account-password-input"
              />
            </div>
            <label className="flex items-start gap-2 text-xs text-slate-400 cursor-pointer">
              <input
                type="checkbox"
                checked={ack}
                onChange={(e) => setAck(e.target.checked)}
                className="mt-0.5 accent-red-500"
                data-testid="delete-account-confirm-checkbox"
              />
              Ich verstehe, dass diese Aktion nicht rückgängig gemacht werden kann
              {isTenant ? " und die Kündigung meines Abonnements ausspricht" : ""}.
            </label>
            <div className="flex gap-2">
              <button
                type="button"
                onClick={() => setConfirmOpen(false)}
                className="btn-ghost flex-1"
                data-testid="delete-account-cancel-button"
              >
                Abbrechen
              </button>
              <button
                type="submit"
                disabled={busy || !ack || !password}
                className="flex-1 inline-flex items-center justify-center rounded-sm bg-red-500 px-4 py-2 text-sm font-semibold text-white hover:bg-red-400 transition-colors disabled:opacity-50"
                data-testid="delete-account-submit-button"
              >
                {busy ? "Lösche…" : "Endgültig löschen"}
              </button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
